import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import React from "react";
import { DateRangeField } from "./DateRangeField";
import { FilterAutocomplete } from "../users/UserFilters";
import { CITY_FILTER_AUTOCOMPLETE, COUNTRY_FILTER_AUTOCOMPLETE, STATE_FILTER_AUTOCOMPLETE } from "../users/users.queries";
import {
  browserTimezone,
  defaultGranularity,
  Granularity,
  MAX_RANGE_DAYS,
  PRESET_DAYS,
  presetRange,
  RangePreset,
} from "./reportFormat";

type RegionOption = { name: string; displayName: string } | null;

export interface ReportFilterState {
  preset: RangePreset;
  from: Date;
  to: Date;
  granularity: Granularity;
  country: RegionOption;
  state: RegionOption;
  city: RegionOption;
}

export function initialReportFilters(): ReportFilterState {
  const { from, to } = presetRange(PRESET_DAYS["30d"]);
  return { preset: "30d", from, to, granularity: "DAY", country: null, state: null, city: null };
}

/** The `range` argument every report query takes. */
export function rangeVariables(filters: ReportFilterState, granularity: Granularity = filters.granularity) {
  return {
    dateFrom: filters.from.toISOString(),
    dateTo: filters.to.toISOString(),
    timezone: browserTimezone(),
    granularity,
    regionCountry: filters.country?.name || null,
    regionState: filters.state?.name || null,
    regionCity: filters.city?.name || null,
  };
}

// Null shows an empty box, as on the region totals; FilterAutocomplete's type says otherwise.
const regionValue = (option: RegionOption) => option as { name: string; displayName: string };
const regionOrNull = (option: { name: string; displayName: string }) => (option?.name ? option : null);

interface Props {
  value: ReportFilterState;
  onChange: (next: ReportFilterState) => void;
  /** Only reports split by period show the control. */
  showGranularity?: boolean;
  /** OTP failures has no member to place, so no region. */
  showRegion?: boolean;
}

/**
 * The range, the period it is split into, and the members' region, in the
 * parameter pane. A change waits for Run.
 */
export const ReportFilters: React.FC<Props> = ({ value, onChange, showGranularity = true, showRegion = true }) => {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
      <DateRangeField
        value={value}
        maxDays={MAX_RANGE_DAYS}
        onChange={({ preset, from, to }) =>
          // "All time" is not offered here, so from and to are always set.
          onChange({ ...value, preset: preset as RangePreset, from: from!, to: to!, granularity: defaultGranularity(from!, to!) })
        }
      />

      {showGranularity && (
        <ToggleButtonGroup
          size="small"
          exclusive
          value={value.granularity}
          onChange={(_, granularity) => granularity && onChange({ ...value, granularity })}
          aria-label="Split into"
        >
          <ToggleButton value="DAY">Days</ToggleButton>
          <ToggleButton value="WEEK">Weeks</ToggleButton>
          <ToggleButton value="MONTH">Months</ToggleButton>
        </ToggleButtonGroup>
      )}

      {showRegion && <RegionFields value={value} onChange={onChange} />}
    </Box>
  );
};

/** Any filter state holding the three region fields. */
type RegionState = Pick<ReportFilterState, "country" | "state" | "city">;

/** The members' country, state and city, as on the users list. */
export function RegionFields<T extends RegionState>({ value, onChange }: { value: T; onChange: (next: T) => void }) {
  return (
    <>
      <Box sx={{ minWidth: 180 }}>
        <FilterAutocomplete
          query={COUNTRY_FILTER_AUTOCOMPLETE}
          label="Country"
          noOptionsText="No country"
          value={regionValue(value.country)}
          optionExtractor={(data) =>
            data.getCountriesAutocomplete.map((country: any) => ({ displayName: country.regionCountry, name: country.regionCountry }))
          }
          onChange={(option) => onChange({ ...value, country: regionOrNull(option) })}
        />
      </Box>
      <Box sx={{ minWidth: 180 }}>
        <FilterAutocomplete
          query={STATE_FILTER_AUTOCOMPLETE}
          label="State"
          noOptionsText="No state"
          value={regionValue(value.state)}
          optionExtractor={(data) =>
            data.getStatesAutocomplete.map((state: any) => ({
              name: state.regionState,
              displayName: `${state.regionState}, ${state.regionCountry}`,
            }))
          }
          onChange={(option) => onChange({ ...value, state: regionOrNull(option) })}
        />
      </Box>
      <Box sx={{ minWidth: 200 }}>
        <FilterAutocomplete
          query={CITY_FILTER_AUTOCOMPLETE}
          label="City"
          noOptionsText="No city"
          value={regionValue(value.city)}
          optionExtractor={(data) =>
            data.getCitiesAutocomplete.map((city: any) => ({
              name: city.regionCity,
              displayName: `${city.regionCity}, ${city.regionState}, ${city.regionCountry}`,
            }))
          }
          onChange={(option) => onChange({ ...value, city: regionOrNull(option) })}
        />
      </Box>
    </>
  );
}
